import { useEffect, useMemo, useRef, useState } from 'react';
import mascot from '../img/the-ball.png';
import wreckoningBall from '../img/wreckoning-ball.png';
import appStoreBadge from '../img/Store=App Store, Type=Dark, Language=English@3x.png';
import googlePlayBadge from '../img/Store=Google Play, Type=Dark, Language=English@3x.png';
import SiteFooter from '../components/SiteFooter';

const BALL_HINTS = [
  'Your daily reading is waiting in a much more committed format.',
  'The browser can only hint at the level of judgment available elsewhere.',
  'Open Wreckoning Ball and receive today’s emotional paperwork.',
  'The Ball is considerably more useful once it has proper access to your habits.',
  'Scroll all twelve signs if your own doom feels too private.',
  'Ask the Ball a question if your judgment needs outside supervision.',
  'Come back tomorrow. The app will have new material about your habits.',
  'This experience improves noticeably once the icon lives on your phone.',
  'The Ball prefers a closer working relationship.',
  'There is, technically, a more direct way to do this.',
];

type HeroBallPhase = 'idle' | 'thinking' | 'typing' | 'holding';
type HeroStar = {
  key: string;
  x: number;
  y: number;
  size: number;
  opacity: number;
  isSparkle: boolean;
};

const DOOM_CAROUSEL_CARDS = [
  {
    title: 'Your daily doom, delivered with unsettling specificity',
    text: 'Each sign gets a reading that feels uncomfortably familiar, which is nice if you enjoy being observed by an app. The tone is clear, concise, and slightly too informed for comfort.',
    src: '/media/simulator-1.png',
    alt: 'Taurus daily doom card showing a behavior-based reading on a gold theme.',
  },
  {
    title: 'Share your bad omens with admirable confidence',
    text: 'Package your daily collapse into something polished enough to post. It is surprisingly elegant for content that amounts to "well, there it is again."',
    src: '/media/simulator-2.png',
    alt: 'Share Your Doom screen with background colors and layout options for a Taurus reading.',
  },
  {
    title: 'Save the readings that felt a little too accurate',
    text: 'Keep the dooms that hit a nerve so you can revisit them later and pretend it is for reflection. It is, of course, mostly evidence collection.',
    src: '/media/simulator-3.png',
    alt: 'Past Traumas screen showing a saved Aquarius reading in a blue theme.',
  },
  {
    title: 'Ask the Ball for guidance, regret the clarity.',
    text: 'When your own judgment is already circling the drain, the Ball is happy to step in with a calm, ominous sentence. It rarely overexplains, which somehow makes it worse.',
    src: '/media/simulator-4.png',
    alt: 'Confer with Ball screen showing the Wreckoning Ball and a typed answer.',
  },
  {
    title: 'Choose a theme for your beautifully curated decline',
    text: 'Pick the palette that best complements your preferred flavor of inevitability. Even minor emotional damage deserves good color management.',
    src: '/media/simulator-5.png',
    alt: 'Theme selection screen listing zodiac-inspired color themes.',
  },
  {
    title: 'A cleaner way to navigate your ongoing unraveling',
    text: 'The menu keeps every category neatly in reach, because disorder should at least be well organized. It is thoughtful, efficient, and deeply uninvested in protecting you from yourself.',
    src: '/media/simulator-6.png',
    alt: 'Side menu with navigation options including Daily Dooms, Confer with Ball, and Profile.',
  },
  {
    title: 'Set up your profile and personalize the damage',
    text: 'Add your details, sign, and login methods so the app can tailor the daily indictment properly. Convenience is important when doom is being customized at scale.',
    src: '/media/simulator-7.png',
    alt: 'Profile screen with username, birthdate, sign, and sign-in method settings.',
  },
];

function getRandomSubset(messages: string[], size: number) {
  const shuffled = [...messages];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled.slice(0, size);
}

function buildHeroStars(count: number): HeroStar[] {
  return Array.from({ length: count }, (_, index) => {
    const x = Math.random() * 100;
    const y = Math.random() * 100;
    const isSparkle = Math.random() < 0.22;
    const size = isSparkle
      ? 2.5 + (Math.random() * 5.5)
      : 0.7 + (Math.random() * 2.2);
    const opacity = isSparkle
      ? 0.35 + (Math.random() * 0.5)
      : 0.14 + (Math.random() * 0.36);

    return {
      key: `hero-star-${index}`,
      x,
      y,
      size,
      opacity,
      isSparkle,
    };
  });
}

function HomeTopBar() {
  return (
    <div className="hero-topbar">
      <div className="hero-brand">
        <img className="gradient-mascot" src={mascot} alt="" aria-hidden="true" />
        <div className="hero-brand-text">
          <p className="hero-title">Wreckoning Ball</p>
        </div>
      </div>
      <p className="hero-subtitle hero-subtitle-right">Woo Adjacent</p>
    </div>
  );
}

export default function HomeScreen() {
  const rotatingMessages = useMemo(() => getRandomSubset(BALL_HINTS, 10), []);
  const heroStars = useMemo(() => buildHeroStars(620), []);
  const [phase, setPhase] = useState<HeroBallPhase>('idle');
  const [messageIndex, setMessageIndex] = useState(0);
  const [typedLength, setTypedLength] = useState(0);
  const [isCarouselDragging, setIsCarouselDragging] = useState(false);
  const currentMessage = rotatingMessages[messageIndex] ?? '';
  const dragStateRef = useRef({
    isDragging: false,
    startX: 0,
    startScrollLeft: 0,
  });

  useEffect(() => {
    if (!currentMessage) {
      return undefined;
    }

    const idleDurationMs = 3000 + Math.floor(Math.random() * 2000);
    const thinkingDurationMs = 1200 + Math.floor(Math.random() * 900);
    let typingTimeoutId: ReturnType<typeof setTimeout> | undefined;
    let thinkingTimeoutId: ReturnType<typeof setTimeout> | undefined;
    let holdingTimeoutId: ReturnType<typeof setTimeout> | undefined;

    setPhase('idle');
    setTypedLength(0);

    const idleTimeoutId = setTimeout(() => {
      setPhase('thinking');

      thinkingTimeoutId = setTimeout(() => {
        setPhase('typing');

        const scheduleNext = (currentLength: number) => {
          if (currentLength >= currentMessage.length) {
            setPhase('holding');
            holdingTimeoutId = setTimeout(() => {
              setMessageIndex((previousIndex) => (previousIndex + 1) % rotatingMessages.length);
            }, 10000);
            return;
          }

          const nextChar = currentMessage[currentLength] ?? '';
          const baseDelay = 74;
          const randomJitter = Math.floor(Math.random() * 48);
          const punctuationPause = /[.,!?]/.test(nextChar) ? 140 : 0;
          const spacePause = nextChar === ' ' ? 25 : 0;
          const nextDelay = baseDelay + randomJitter + punctuationPause + spacePause;

          typingTimeoutId = setTimeout(() => {
            setTypedLength(currentLength + 1);
            scheduleNext(currentLength + 1);
          }, nextDelay);
        };

        scheduleNext(0);
      }, thinkingDurationMs);
    }, idleDurationMs);

    return () => {
      clearTimeout(idleTimeoutId);
      if (thinkingTimeoutId) {
        clearTimeout(thinkingTimeoutId);
      }
      if (holdingTimeoutId) {
        clearTimeout(holdingTimeoutId);
      }
      if (typingTimeoutId) {
        clearTimeout(typingTimeoutId);
      }
    };
  }, [currentMessage, rotatingMessages.length]);

  const handleCarouselPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse') {
      return;
    }
    const node = event.currentTarget;
    dragStateRef.current.isDragging = true;
    dragStateRef.current.startX = event.clientX;
    dragStateRef.current.startScrollLeft = node.scrollLeft;
    setIsCarouselDragging(true);
    node.setPointerCapture(event.pointerId);
  };

  const handleCarouselPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current.isDragging) {
      return;
    }

    const node = event.currentTarget;
    const deltaX = event.clientX - dragStateRef.current.startX;
    node.scrollLeft = dragStateRef.current.startScrollLeft - deltaX;
  };

  const snapCarouselToNearestCard = (node: HTMLDivElement) => {
    const cards = Array.from(node.querySelectorAll<HTMLElement>('.doom-carousel-card'));
    if (cards.length === 0) {
      return;
    }

    const containerCenter = node.scrollLeft + (node.clientWidth / 2);
    let nearestCard = cards[0];
    let nearestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card) => {
      const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
      const distance = Math.abs(cardCenter - containerCenter);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestCard = card;
      }
    });

    const targetLeft = nearestCard.offsetLeft - ((node.clientWidth - nearestCard.offsetWidth) / 2);
    node.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: 'smooth',
    });
  };

  const handleCarouselPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current.isDragging) {
      return;
    }
    dragStateRef.current.isDragging = false;
    setIsCarouselDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    snapCarouselToNearestCard(event.currentTarget);
  };

  return (
    <div className="app">
      <main id="main-content" tabIndex={-1}>
        <header className="hero">
          <div className="hero-stars-layer" aria-hidden="true">
            {heroStars.map((star) => (
              <div
                key={star.key}
                className="hero-star-wrap"
                style={{
                  left: `${star.x}%`,
                  top: `${star.y}%`,
                  width: `${star.size}px`,
                  height: `${star.size}px`,
                  opacity: star.opacity,
                }}
              >
                {star.isSparkle ? (
                  <>
                    <span className="hero-star-ray hero-star-ray-horizontal" />
                    <span className="hero-star-ray hero-star-ray-vertical" />
                    <span className="hero-star-core" />
                  </>
                ) : (
                  <span className="hero-star-dot" />
                )}
              </div>
            ))}
          </div>
          <div className="content-well hero-shell">
            <HomeTopBar />
            <div className="hero-stage">
              <div className="hero-ball-column" aria-live="polite">
                <div className="hero-ball-frame">
                  <div className="hero-ball-visual">
                    <div className="hero-ball-float-wrap">
                      <img className="hero-ball-image" src={wreckoningBall} alt="The Wreckoning Ball" />
                      <div className={`hero-ball-dots hero-ball-dots-overlay ${phase === 'thinking' ? 'is-thinking' : ''}`}>
                        <span />
                        <span />
                        <span />
                      </div>
                    </div>
                  </div>
                  <div className="hero-ball-panel">
                    <p className={`hero-ball-message ${phase === 'typing' || phase === 'holding' ? 'is-visible' : ''}`}>
                      {currentMessage.slice(0, typedLength)}
                      {phase === 'typing' ? <span className="hero-ball-cursor">_</span> : null}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="store-badges hero-download-badges hero-download-badges-bottom">
              <a href="https://apps.apple.com/app/id6760224604" aria-label="Download on the App Store">
                <img src={appStoreBadge} alt="Download on the App Store" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.wreckoningball.app" aria-label="Get it on Google Play">
                <img src={googlePlayBadge} alt="Get it on Google Play" />
              </a>
            </div>
          </div>
        </header>

      <section className="feature-section section-white">
        <div className="content-well feature-grid feature-grid-single">
          <article className="feature-copy feature-copy-wide">
            <h2>This is not a traditional horoscope app.</h2>
            <p className="feature-definition feature-definition-spaced">
              The Wreckoning Ball delivers short, daily readings inspired by astrology but grounded in observation, not prediction.
            </p>
            <p className="feature-definition feature-definition-spaced">
              Each sign receives a concise, behavior-based reading that reflects familiar patterns, small decisions, and everyday outcomes with uncomfortable accuracy.
            </p>
            <p className="feature-definition">These are not predictions of what might happen.</p>
            <p className="feature-definition feature-definition-spaced">They are observations of what tends to happen.</p>
            <p className="feature-definition">No mysticism.</p>
            <p className="feature-definition">No vague advice.</p>
            <p className="feature-definition feature-definition-spaced">No forced positivity.</p>
            <p className="feature-definition feature-definition-spaced">
              Just clear observations of what people tend to do, written as if it were already decided.
            </p>
            <p className="feature-definition">You may recognize the moment before it happens.</p>
            <p className="feature-definition feature-definition-spaced">You may recognize it after.</p>
            <p className="feature-definition feature-definition-spaced">Scroll through all twelve signs or go straight to your own.</p>
            <p className="feature-definition feature-definition-spaced">New readings appear daily.</p>
            <h3>Features:</h3>
            <ul className="feature-definition-list">
              <li>12 daily readings, one for each sign</li>
              <li>Clean, minimal interface</li>
              <li>Short, highly shareable format</li>
              <li>New content every 24 hours</li>
              <li>Ask the Ball a question and receive a calm, slightly unsettling answer</li>
            </ul>
            <p className="feature-definition feature-definition-spaced">The Wreckoning Ball doesn&apos;t tell you what might happen.</p>
            <p className="feature-definition feature-definition-spaced">It reflects what was already in motion.</p>
            <p className="feature-definition">The Ball&apos;s insights will expand.</p>
          </article>
        </div>
      </section>

      <section className="feature-section section-silver">
        <div
          className={`doom-carousel ${isCarouselDragging ? 'is-dragging' : ''}`}
          onPointerDown={handleCarouselPointerDown}
          onPointerMove={handleCarouselPointerMove}
          onPointerUp={handleCarouselPointerUp}
          onPointerCancel={handleCarouselPointerUp}
        >
          {DOOM_CAROUSEL_CARDS.map((card) => (
            <article key={card.src} className="doom-carousel-card">
              <div className="doom-carousel-card-body">
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </div>
              <div className="doom-carousel-card-media">
                <img src={card.src} alt={card.alt} />
              </div>
            </article>
          ))}
        </div>
      </section>
      </main>

      <SiteFooter />
    </div>
  );
}
